import { useContext, useState } from "react";

import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { usePapaParse } from "react-papaparse";

import _ from "lodash";

import { BannerType } from "@dotnet/telematics-design-system";

import DropzoneContext from "context/dropzone/dropzoneContext";
import { Message } from "context/dropzone/types/interfaces";

import Frame from "../Frame";
import { DataProps, SingleFileProps } from "../types";
import UploadedFileSingle from "./UploadedFileSingle";

const SingleFile = ({
    supportedFormats,
    isLoading,
    isDisabled,
    maxFileNameLength,
    countCsv,
    tooltipText
}: SingleFileProps) => {
    const { t } = useTranslation();

    const { file, setFile, setMessage } = useContext(DropzoneContext);

    const { readString } = usePapaParse();

    const [isDragged, setIsDragged] = useState(false);

    const { getRootProps, getInputProps } = useDropzone({
        accept: { "application/x-file": supportedFormats },
        disabled: isDisabled,
        multiple: false,
        onDragEnter: () => setIsDragged(true),
        onDragLeave: () => setIsDragged(false),
        onDrop: () => setIsDragged(false),
        onDropAccepted: acceptedFiles => {
            const acceptedFile = acceptedFiles[0];

            const isCorrectNameLength = maxFileNameLength
                ? acceptedFile.name.length <= maxFileNameLength
                : true;

            if (countCsv) {
                const reader = new FileReader();

                reader.readAsText(acceptedFile);

                reader.onload = () => {
                    readString(reader.result as string, {
                        worker: true,
                        delimiter: ",",
                        skipEmptyLines: true,
                        complete: ({ data }: DataProps) => {
                            isCorrectNameLength && setFile(acceptedFile, data);
                        }
                    });
                };
            } else {
                isCorrectNameLength && setFile(acceptedFile);
            }

            !isCorrectNameLength &&
                setMessage(
                    {
                        status: BannerType.Error,
                        title: t("Dropzone##too long file name", {
                            maxLength: maxFileNameLength
                        })
                    },
                    true
                );
        },
        onDropRejected: rejected => {
            const errorCodes = rejected.reduce(
                (previous: string[], current) => {
                    const currentErrors = current.errors.map(
                        error => error.code
                    );

                    return [...previous, ...currentErrors];
                },
                []
            );

            const uniqueErrorCodes = _.uniq(errorCodes);

            const errors: Message[] = uniqueErrorCodes.map(errorCode => {
                if (errorCode === "file-invalid-type") {
                    return {
                        status: BannerType.Error,
                        title: t("Dropzone##file invalid type")
                    };
                } else {
                    return {
                        status: BannerType.Error,
                        title: t("Dropzone##only one file can be attached")
                    };
                }
            });

            setMessage(errors, true);
        }
    });

    return file ? (
        <UploadedFileSingle isLoading={isLoading} countCsv={countCsv} />
    ) : (
        <Frame
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isDragged={isDragged}
            supportedFormats={supportedFormats}
            isDisabled={isDisabled}
            tooltipText={tooltipText}
        />
    );
};

export default SingleFile;
